import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { T } from '@tolgee/react';
import { useProductSearch, useCategories } from '../../../lib/api/hooks.tsx';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';
import Breadcrumbs from '../components/Breadcrumbs';
import Pagination from '../components/Pagination';
import ProductCard from '../components/ProductCard';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const queryParam = searchParams.get('q');
  const brandParam = searchParams.get('brand');
  const tagsParam = searchParams.get('tags');
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [currentPage, setCurrentPage] = useState<number>(pageParam);
  const [sortType, setSortType] = useState<string>('featured');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(
    new Set(brandParam ? brandParam.split(',') : [])
  );
  const [selectedTags, setSelectedTags] = useState<Set<string>>(
    new Set(tagsParam ? tagsParam.split(',') : [])
  );
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);
  const headerRef = useRef(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);

  // Detectar scroll para mostrar/ocultar botón flotante de filtros
  useEffect(() => {
    const handleScroll = () => {
      if (filterButtonRef.current) {
        const buttonRect = filterButtonRef.current.getBoundingClientRect();
        // Mostrar flotante cuando el botón estático sale de la vista
        setShowFloatingFilter(buttonRect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hook de Zaraz tracking
  const { trackProductListViewed, trackProductClicked } = useZarazTracking();

  // Usar hooks de la nueva API con Meilisearch
  const { data: categories } = useCategories();

  // Usar el nuevo hook de búsqueda con Meilisearch
  const {
    data: products,
    loading,
    error,
    refetch,
    pagination,
    hybridSearch,
    facets
  } = useProductSearch({
    query: queryParam || '',
    category: categoryParam || undefined,
    brand: selectedBrands.size > 0 ? Array.from(selectedBrands).join(',') : undefined,
    tags: selectedTags.size > 0 ? Array.from(selectedTags) : undefined,
    page: currentPage,
    limit: 25,
    sort: sortType !== 'featured' ? sortType : undefined
  });

  // Sincronizar página con URL
  useEffect(() => {
    setCurrentPage(pageParam);
  }, [pageParam]);

  // Auto-expandir categorías padre cuando hay una categoría activa
  useEffect(() => {
    if (categoryParam && facets && facets.categoryPath) {
      const newExpanded = new Set<string>();

      // Buscar el path completo de la categoría activa
      Object.keys(facets.categoryPath).forEach(path => {
        // Si coincide con la categoría activa (usa fullPath ej: "AVES > ALIMENTACIÓN")
        if (path === categoryParam) {
          const parts = path.split(' > ');

          // Expandir todos los niveles padre
          let parentFullPath = '';
          for (let i = 0; i < parts.length - 1; i++) {
            parentFullPath = parentFullPath ? `${parentFullPath} > ${parts[i]}` : parts[i];
            newExpanded.add(parentFullPath);
          }
        }
      });

      setExpandedCategories(newExpanded);
    }
  }, [categoryParam, facets]);

  // Trackear vista de lista de productos
  useEffect(() => {
    if (products) {
      const listName = queryParam
        ? `Búsqueda: ${queryParam}`
        : categoryParam
          ? `Categoría: ${categoryParam}`
          : 'Todos los productos';
      trackProductListViewed(products, listName);
    }
  }, [products, categoryParam, queryParam, trackProductListViewed]);

  // Manejar cambio de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Actualizar URL con el nuevo número de página
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para cambiar categoría (filtra vía Meilisearch)
  const handleCategoryChange = (categoryPath: string, categoryNode?: any) => {
    const newParams = new URLSearchParams(searchParams);
    if (categoryPath === 'all') {
      newParams.delete('category');
      // Colapsar todas las categorías al seleccionar "Todos"
      setExpandedCategories(new Set());
    } else {
      // Enviar el path jerárquico completo para filtrar en Meilisearch (e.g. "AVES > ALIMENTACIÓN")
      newParams.set('category', categoryPath);

      // Siempre expandir la categoría seleccionada si tiene hijos
      if (categoryNode && categoryNode.children && categoryNode.children.length > 0) {
        setExpandedCategories(prev => {
          const newSet = new Set(prev);
          // Añadir la categoría actual
          newSet.add(categoryNode.fullPath);
          return newSet;
        });
      }

      // Si es una categoría padre (no contiene " > "), también expandirla
      if (!categoryPath.includes(' > ')) {
        setExpandedCategories(prev => {
          const newSet = new Set(prev);
          newSet.add(categoryPath);
          return newSet;
        });
      }
    }
    newParams.set('page', '1'); // Resetear a página 1
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Función para cambiar ordenamiento
  const handleSortChange = (newSortType: string) => {
    setSortType(newSortType);
    // El hook useProductSearch se encargará de actualizar los datos
  };

  // Función para toggle marca
  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(brand)) {
        newSet.delete(brand);
      } else {
        newSet.add(brand);
      }

      // Actualizar URL
      const newParams = new URLSearchParams(searchParams);
      if (newSet.size > 0) {
        newParams.set('brand', Array.from(newSet).join(','));
      } else {
        newParams.delete('brand');
      }
      newParams.set('page', '1');
      setSearchParams(newParams);

      return newSet;
    });
  };

  // Función para toggle tag
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }

      // Actualizar URL
      const newParams = new URLSearchParams(searchParams);
      if (newSet.size > 0) {
        newParams.set('tags', Array.from(newSet).join(','));
      } else {
        newParams.delete('tags');
      }
      newParams.set('page', '1');
      setSearchParams(newParams);

      return newSet;
    });
  };

  // Función para limpiar todos los filtros
  const clearAllFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('category');
    newParams.delete('brand');
    newParams.delete('tags');
    newParams.set('page', '1');
    setSearchParams(newParams);
    setCurrentPage(1);
    setSelectedBrands(new Set());
    setSelectedTags(new Set());
  };

  // Helper: Toggle expandir/colapsar categoría
  const toggleCategory = (slug: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        newSet.add(slug);
      }
      return newSet;
    });
  };

  // Helper: Construir árbol de categorías desde facets de categoryPath
  const buildCategoryTree = () => {
    if (!facets || !facets.categoryPath) return [];

    // Siempre mostrar todas las categorías (el backend ya devuelve facets completos)
    const allFacets = facets.categoryPath;

    // Estructura: { name, slug, fullPath, fullSlugPath, count, children: [] }
    const tree: any[] = [];
    const pathMap = new Map();

    // Procesar cada path del facet
    Object.entries(allFacets).forEach(([path, count]) => {
      const parts = path.split(' > ');
      let currentLevel = tree;
      let currentPath = '';
      let currentSlugPath = '';

      parts.forEach((name, index) => {
        currentPath = currentPath ? `${currentPath} > ${name}` : name;
        const slug = name.toLowerCase().replace(/\s+/g, '-');
        currentSlugPath = currentSlugPath ? `${currentSlugPath}-${slug}` : slug;

        // Buscar si ya existe en este nivel usando el path completo
        let existing = currentLevel.find((cat: any) => cat.fullPath === currentPath);

        if (!existing) {
          existing = {
            name,
            slug,
            fullPath: currentPath, // "Aves > Accesorios"
            fullSlugPath: currentSlugPath, // "aves-accesorios"
            count: index === parts.length - 1 ? count : 0,
            children: []
          };
          currentLevel.push(existing);
          pathMap.set(currentPath, existing);
        } else if (index === parts.length - 1) {
          // Si es el último nivel, actualizar el count
          existing.count = count;
        }

        currentLevel = existing.children;
      });
    });

    return tree;
  };

  // Helper: Obtener conteo para un path específico
  const getCategoryPathCount = (categorySlug: string): number => {
    if (!facets || !facets.categoryPath) return 0;

    // Buscar el path que corresponde al slug
    for (const [path, count] of Object.entries(facets.categoryPath)) {
      const pathSlug = path.split(' > ').pop()?.toLowerCase().replace(/\s+/g, '-');
      if (pathSlug === categorySlug) {
        return count as number;
      }
    }
    return 0;
  };

  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  // Mensaje de error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8 max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            <T keyName="products.error_loading">Error al cargar productos</T>
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
          >
            <T keyName="common.retry">Reintentar</T>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* SEO dinámico para PLP */}
      <script dangerouslySetInnerHTML={{
        __html: `
        (function(){
          var t = document.querySelector('title');
          if(t) t.textContent = ${JSON.stringify(`${categoryParam ? categoryParam.charAt(0).toUpperCase() + categoryParam.slice(1) : 'Productos'} | Jarad Pet Food`)};
          var d = document.querySelector('meta[name="description"]');
          if(d) d.setAttribute('content', 'Explora nuestro catálogo de productos Jarad Pet Food.');
          // BreadcrumbList schema (simple)
          var ld = {
            "@context":"https://schema.org",
            "@type":"BreadcrumbList",
            "itemListElement":[
              {"@type":"ListItem","position":1,"name":"Inicio","item":"https://jaradpetfood.com/"},
              {"@type":"ListItem","position":2,"name":"Productos","item":"https://jaradpetfood.com/products"}
            ]
          };
          var n = document.createElement('script');
          n.type='application/ld+json';
          n.text=JSON.stringify(ld);
          document.head.appendChild(n);
        })();
      `}} />

      {/* Header compacto y profesional */}
      <div
        ref={headerRef}
        className="border-b border-gray-200 bg-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {categoryParam ? (
            <div className="mb-3">
              <Breadcrumbs categoryId={categoryParam} />
            </div>
          ) : null}

          <div className="flex items-center justify-between">
            <div>
              {categoryParam && categoryParam.includes('>') ? (
                <>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                    {categoryParam.split('>')[0].trim()}
                  </p>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {categoryParam.split('>').pop()!.trim()}
                  </h1>
                </>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {queryParam ? (
                    `Resultados para "${queryParam}"`
                  ) : categoryParam ? (
                    categoryParam.charAt(0).toUpperCase() + categoryParam.slice(1)
                  ) : 'Todos los productos'}
                </h1>
              )}
              <p className="text-sm text-gray-500">
                {loading ? <T keyName="common.loading">Cargando...</T> : (
                  <>
                    {pagination?.total || 0} {(pagination?.total || 0) === 1 ? 'resultado' : 'resultados'}
                    {hybridSearch && (
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        Búsqueda IA
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de filtros móvil - Full width debajo del header */}
      <div ref={filterButtonRef} className="lg:hidden border-b border-gray-200 bg-white">
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <button
            onClick={() => setIsFilterSheetOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="font-medium">Filtros y ordenar</span>
            {(categoryParam || selectedBrands.size > 0 || selectedTags.size > 0) && (
              <span className="bg-black text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {(categoryParam ? 1 : 0) + selectedBrands.size + selectedTags.size}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Layout con Sidebar y Productos */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar de Filtros - Solo visible en desktop */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Categorías */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    <T keyName="sidebar.categories">Categorías</T>
                  </h3>
                  {categoryParam && (
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      title="Ver todas las categorías"
                    >
                      Ver todas
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${!categoryParam
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="sidebar.view_all_products">Todos los productos</T>
                  </button>

                  {/* Renderizar árbol de categorías desde facets */}
                  {(() => {
                    const categoryTree = buildCategoryTree();

                    // Componente recursivo para renderizar categorías
                    const renderCategoryNode = (node: any, level: number = 0) => {
                      const hasChildren = node.children && node.children.length > 0;
                      const isActive = categoryParam === node.fullPath;
                      const isExpanded = expandedCategories.has(node.fullPath);

                      return (
                        <div key={node.fullPath} className={level > 0 ? 'ml-3' : ''}>
                          <div className="flex items-center gap-1">
                            {/* Botón de expandir/colapsar (solo si tiene hijos) */}
                            {hasChildren && (
                              <button
                                onClick={() => toggleCategory(node.fullPath)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                aria-label={isExpanded ? 'Colapsar' : 'Expandir'}
                              >
                                <svg
                                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                    }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            )}

                            {/* Botón de seleccionar categoría */}
                            <button
                              onClick={() => handleCategoryChange(node.fullPath, node)}
                              className={`flex-1 text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${isActive
                                ? 'bg-black text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                                } ${level > 0 ? 'text-xs' : ''} ${!hasChildren && level > 0 ? 'ml-5' : ''}`}
                            >
                              <span>{node.name}</span>
                              {node.count > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive
                                  ? 'bg-white/20 text-white'
                                  : 'bg-gray-200 text-gray-600'
                                  }`}>
                                  {node.count}
                                </span>
                              )}
                            </button>
                          </div>

                          {/* Renderizar hijos si están expandidos */}
                          {hasChildren && isExpanded && (
                            <div className="mt-1 space-y-1 overflow-hidden">
                              {node.children.map((child: any) => renderCategoryNode(child, level + 1))}
                            </div>
                          )}
                        </div>
                      );
                    };

                    return categoryTree.length > 0 ? (
                      <div className="space-y-1">
                        {categoryTree.map(node => renderCategoryNode(node, 0))}
                      </div>
                    ) : (
                      // Fallback cuando no hay facets aún
                      categories && categories.length > 0 ? (
                        categories.map(category => {
                          const count = getCategoryPathCount(category.slug);
                          return (
                            <button
                              key={category.id}
                              onClick={() => handleCategoryChange(category.name.toUpperCase())}
                              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${categoryParam === category.name.toUpperCase()
                                ? 'bg-black text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              <span>{category.name}</span>
                              {count > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${categoryParam === category.name.toUpperCase()
                                  ? 'bg-white/20 text-white'
                                  : 'bg-gray-200 text-gray-600'
                                  }`}>
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        ['AVES', 'ROEDORES', 'PERROS', 'GATOS'].map(category => (
                          <button
                            key={category}
                            onClick={() => handleCategoryChange(category)}
                            className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${categoryParam === category
                              ? 'bg-black text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()}
                          </button>
                        ))
                      )
                    );
                  })()}
                </div>
              </div>

              {/* Ordenar por */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  <T keyName="category.sort_by">Ordenar por</T>
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleSortChange('featured')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${sortType === 'featured'
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="category.featured">Destacados</T>
                  </button>
                  <button
                    onClick={() => handleSortChange('price-asc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${sortType === 'price-asc'
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="category.price_low_high">Precio: Menor a mayor</T>
                  </button>
                  <button
                    onClick={() => handleSortChange('price-desc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${sortType === 'price-desc'
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="category.price_high_low">Precio: Mayor a menor</T>
                  </button>
                  <button
                    onClick={() => handleSortChange('name-asc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${sortType === 'name-asc'
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="category.name_a_z">Nombre: A-Z</T>
                  </button>
                  <button
                    onClick={() => handleSortChange('name-desc')}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${sortType === 'name-desc'
                      ? 'bg-black text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <T keyName="category.name_z_a">Nombre: Z-A</T>
                  </button>
                </div>
              </div>

              {/* Filtros de Marcas */}
              {facets && facets.brand && Object.keys(facets.brand).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    <T keyName="sidebar.brands">Marcas</T>
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(facets.brand)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .map(([brand, count]) => (
                        <label key={brand} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedBrands.has(brand)}
                            onChange={() => toggleBrand(brand)}
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="text-sm text-gray-700 flex-1">{brand}</span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {count}
                          </span>
                        </label>
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Filtros de Tags */}
              {facets && facets.tags && Object.keys(facets.tags).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    <T keyName="sidebar.tags">Etiquetas</T>
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(facets.tags)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .map(([tag, count]) => (
                        <label key={tag} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedTags.has(tag)}
                            onChange={() => toggleTag(tag)}
                            className="rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span className="text-sm text-gray-700 flex-1">{tag}</span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {count}
                          </span>
                        </label>
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Botón Limpiar Filtros */}
              {(categoryParam || selectedBrands.size > 0 || selectedTags.size > 0) && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <button
                    onClick={clearAllFilters}
                    className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <T keyName="sidebar.clear_filters">Limpiar filtros</T>
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Botón de filtros móvil flotante - Solo aparece al hacer scroll */}
          {showFloatingFilter && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="lg:hidden fixed bottom-4 left-4 z-40"
            >
              <button
                onClick={() => setIsFilterSheetOpen(true)}
                className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full shadow-lg hover:bg-gray-800 transition-all"
              >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="font-medium">Filtros</span>
              {(categoryParam || selectedBrands.size > 0 || selectedTags.size > 0) && (
                <span className="bg-white text-black text-xs font-bold px-2 py-0.5 rounded-full">
                  {(categoryParam ? 1 : 0) + selectedBrands.size + selectedTags.size}
                </span>
              )}
              </button>
            </motion.div>
          )}

          {/* Bottom Sheet de Filtros - Estilo iOS */}
          {isFilterSheetOpen && (
            <div className="lg:hidden fixed inset-0 z-50">
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/40"
                onClick={() => setIsFilterSheetOpen(false)}
              />

              {/* Sheet */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'tween', duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="absolute bottom-0 left-0 right-0 bg-white max-h-[90vh] overflow-hidden flex flex-col"
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-2 pb-1">
                  <div className="w-9 h-1 bg-gray-400/50 rounded-full" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2">
                  <button
                    onClick={() => {
                      clearAllFilters();
                    }}
                    className="text-[17px] text-black active:text-gray-600"
                    style={{ minWidth: '80px' }}
                  >
                    Limpiar
                  </button>
                  <span className="text-[17px] font-semibold text-gray-900">Filtros</span>
                  <button
                    onClick={() => setIsFilterSheetOpen(false)}
                    className="text-[17px] font-semibold text-black active:text-gray-600"
                    style={{ minWidth: '80px', textAlign: 'right' }}
                  >
                    Listo
                  </button>
                </div>

                {/* Contenido scrolleable */}
                <div className="flex-1 overflow-y-auto bg-gray-50">
                  {/* Sección: Ordenar por */}
                  <div className="mt-4">
                    <div className="px-4 pb-2">
                      <span className="text-[13px] font-normal text-gray-500 uppercase tracking-wide">Ordenar por</span>
                    </div>
                    <div className="bg-white border-y border-gray-200">
                      {[
                        { value: 'featured', label: 'Destacados' },
                        { value: 'price-asc', label: 'Precio: Menor a mayor' },
                        { value: 'price-desc', label: 'Precio: Mayor a menor' },
                        { value: 'name-asc', label: 'Nombre: A-Z' },
                        { value: 'name-desc', label: 'Nombre: Z-A' }
                      ].map((option, idx, arr) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full flex items-center justify-between px-4 py-[11px] active:bg-gray-100 ${idx < arr.length - 1 ? 'border-b border-gray-200' : ''}`}
                        >
                          <span className="text-[17px] text-gray-900">{option.label}</span>
                          {sortType === option.value && (
                            <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sección: Categorías */}
                  <div className="mt-8">
                    <div className="px-4 pb-2 flex items-center justify-between">
                      <span className="text-[13px] font-normal text-gray-500 uppercase tracking-wide">Categoría</span>
                      {categoryParam && (
                        <button
                          onClick={() => handleCategoryChange('all')}
                          className="text-[13px] text-black"
                        >
                          Ver todas
                        </button>
                      )}
                    </div>
                    <div className="bg-white border-y border-gray-200">
                      <button
                        onClick={() => {
                          handleCategoryChange('all');
                          setIsFilterSheetOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-[11px] active:bg-gray-100 border-b border-gray-200`}
                      >
                        <span className="text-[17px] text-gray-900">Todos los productos</span>
                        {!categoryParam && (
                          <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>

                      {/* Categorías del árbol */}
                      {(() => {
                        const categoryTree = buildCategoryTree();

                        const renderMobileCategoryNode = (node: any, level: number = 0, isLast: boolean = false) => {
                          const hasChildren = node.children && node.children.length > 0;
                          const isActive = categoryParam === node.fullPath;
                          const isExpanded = expandedCategories.has(node.fullPath);

                          return (
                            <div key={node.fullPath}>
                              <button
                                onClick={() => {
                                  if (hasChildren) {
                                    toggleCategory(node.fullPath);
                                  }
                                  handleCategoryChange(node.fullPath, node);
                                  if (!hasChildren) {
                                    setIsFilterSheetOpen(false);
                                  }
                                }}
                                className={`w-full flex items-center justify-between px-4 py-[11px] active:bg-gray-100 ${!isLast || hasChildren ? 'border-b border-gray-200' : ''}`}
                                style={{ paddingLeft: `${16 + level * 20}px` }}
                              >
                                <div className="flex items-center gap-2">
                                  {hasChildren && (
                                    <svg
                                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  <span className={`text-[17px] ${level > 0 ? 'text-gray-600' : 'text-gray-900'}`}>{node.name}</span>
                                  {node.count > 0 && (
                                    <span className="text-[15px] text-gray-400">{node.count}</span>
                                  )}
                                </div>
                                {isActive && (
                                  <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                              {hasChildren && isExpanded && (
                                <div>
                                  {node.children.map((child: any, idx: number) =>
                                    renderMobileCategoryNode(child, level + 1, idx === node.children.length - 1)
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        };

                        return categoryTree.length > 0 ? (
                          <>
                            {categoryTree.map((node, idx) => renderMobileCategoryNode(node, 0, idx === categoryTree.length - 1))}
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Sección: Marcas */}
                  {facets && facets.brand && Object.keys(facets.brand).length > 0 && (
                    <div className="mt-8">
                      <div className="px-4 pb-2">
                        <span className="text-[13px] font-normal text-gray-500 uppercase tracking-wide">Marcas</span>
                      </div>
                      <div className="bg-white border-y border-gray-200">
                        {Object.entries(facets.brand)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .map(([brand, count], idx, arr) => (
                            <label
                              key={brand}
                              className={`flex items-center justify-between px-4 py-[11px] active:bg-gray-100 cursor-pointer ${idx < arr.length - 1 ? 'border-b border-gray-200' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-[17px] text-gray-900">{brand}</span>
                                <span className="text-[15px] text-gray-400">{count}</span>
                              </div>
                              <div className={`w-[22px] h-[22px] border-2 flex items-center justify-center ${selectedBrands.has(brand) ? 'bg-black border-black' : 'border-gray-300'}`}>
                                {selectedBrands.has(brand) && (
                                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={selectedBrands.has(brand)}
                                onChange={() => toggleBrand(brand)}
                                className="sr-only"
                              />
                            </label>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Sección: Etiquetas */}
                  {facets && facets.tags && Object.keys(facets.tags).length > 0 && (
                    <div className="mt-8 mb-8">
                      <div className="px-4 pb-2">
                        <span className="text-[13px] font-normal text-gray-500 uppercase tracking-wide">Etiquetas</span>
                      </div>
                      <div className="bg-white border-y border-gray-200">
                        {Object.entries(facets.tags)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .map(([tag, count], idx, arr) => (
                            <label
                              key={tag}
                              className={`flex items-center justify-between px-4 py-[11px] active:bg-gray-100 cursor-pointer ${idx < arr.length - 1 ? 'border-b border-gray-200' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-[17px] text-gray-900">{tag}</span>
                                <span className="text-[15px] text-gray-400">{count}</span>
                              </div>
                              <div className={`w-[22px] h-[22px] border-2 flex items-center justify-center ${selectedTags.has(tag) ? 'bg-black border-black' : 'border-gray-300'}`}>
                                {selectedTags.has(tag) && (
                                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={selectedTags.has(tag)}
                                onChange={() => toggleTag(tag)}
                                className="sr-only"
                              />
                            </label>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Espaciado inferior para el safe area */}
                  <div className="h-8" />
                </div>

                {/* Footer con contador */}
                <div className="bg-white border-t border-gray-200 px-4 py-3 pb-8">
                  <button
                    onClick={() => setIsFilterSheetOpen(false)}
                    className="w-full py-[14px] bg-black text-white text-[17px] font-semibold active:bg-gray-800"
                  >
                    Ver {pagination?.total || 0} productos
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Área de Productos */}
          <main className="flex-1 min-w-0">
            {loading ? (
              // Skeleton loader para productos
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {Array.from({ length: 9 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-200 rounded-lg aspect-square mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : !products || products.length === 0 ? (
              // Mensaje de no hay productos
              <div className="text-center py-16">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">
                  <T keyName="category.no_products_found">No se encontraron productos</T>
                </h3>
                <p className="text-gray-500 mb-8">
                  <T keyName="products.try_other_filters">Intenta con otros filtros o categorías</T>
                </p>
                <button
                  onClick={() => handleCategoryChange('all')}
                  className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                >
                  <T keyName="sidebar.view_all_products">Ver todos los productos</T>
                </button>
              </div>
            ) : (
              <>
                {/* Grid de productos */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
                >
                  {products.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      index={index}
                      onProductClick={trackProductClicked}
                    />
                  ))}
                </motion.div>

                {/* Paginación */}
                {pagination && pagination.pages > 1 ? (
                  <div className="mt-12">
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.pages}
                      onPageChange={handlePageChange}
                      totalItems={pagination.total}
                      itemsPerPage={pagination.limit}
                    />
                  </div>
                ) : null}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
